import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { IoCaretDownSharp, IoCaretUpSharp, IoEye } from 'react-icons/io5'
import SummonerSpellIconWithFallback from '../shared/SummonerSpellIcon';
import AugmentIconWithFallback from '../shared/AugmentIcon';
import BuildAndTimeline from './BuildAndTimeline';
import TeamGoldChart from './TeamGoldChart';
import ParticipantItemIconsWithFallBack from '../shared/ParticipantItemIcons';
import { assetUrl, checkIfChampionIsKaynTransformation } from '../helper_functions/utilities';
import { AugmentGrid, CompactRankLabel, getToneStyles, KdaValue, PerformanceRankBadge, RuneIcons, SpellIcons, ToneBadge } from './match/MatchSharedElements';
import { buttonFocusRingClass } from '../shared/buttonStyles';

const MOBILE_CARD_CHROME_INNER_CLASS_NAME = 'px-2.5 py-1.5';
const MOBILE_DETAIL_STAT_CARD_CLASS_NAME = 'rounded-lg border border-zinc-800 bg-zinc-950/90 px-2 py-1 text-[10px] text-zinc-300';
const MOBILE_DETAIL_SMALL_PILL_CLASS_NAME = 'flex h-[22px] w-full min-w-0 items-center justify-between gap-2 rounded-full border border-zinc-800 bg-zinc-950/85 px-2 text-[10px] text-zinc-300';
const MOBILE_DETAIL_STAT_MATRIX_CLASS_NAME = 'grid w-max grid-cols-[max-content_max-content_max-content_max-content] grid-rows-2 gap-0.5';
const MOBILE_DETAIL_METRIC_LINE_CLASS_NAME = 'flex items-center gap-1 leading-none';
const MOBILE_DETAIL_ICON_ROW_CLASS_NAME = 'h-5 w-[42px] items-center justify-between overflow-hidden';
const LEGACY_ARENA_QUEUE_ID = 1700
const TRIOS_ARENA_QUEUE_ID = 1750

function getMatchTone(match, participant) {
    if (match?.info?.endOfGameResult === "Abort_Unexpected" || participant?.gameEndedInEarlySurrender) {
        return 'remake'
    }

    return participant?.win ? 'victory' : 'defeat'
}

function isArenaQueue(queueId) {
    return Number(queueId) === LEGACY_ARENA_QUEUE_ID || Number(queueId) === TRIOS_ARENA_QUEUE_ID
}

function isTriosArenaQueue(queueId) {
    return Number(queueId) === TRIOS_ARENA_QUEUE_ID
}

function getArenaVictoryPlacement(queueId) {
    return isTriosArenaQueue(queueId) ? 3 : 4
}

function formatKdaValue(participant) {
    const kdaValue = Number(participant?.challenges?.kda || 0)

    if (Boolean(participant?.challenges?.perfectGame) && kdaValue !== 0) {
        return 'Perfect'
    }

    return kdaValue.toFixed(1).replace(/\.0$/, '')
}

function formatGoldValue(participant) {
    const value = (Number(participant?.goldEarned || 0) / 1000).toFixed(1)
    return `${value.replace(/\.0$/, '')}k`
}

function formatCompactRateValue(value) {
    return Number(value || 0).toFixed(1).replace(/\.0$/, '')
}

function getPerformanceBadgeStyles(label) {
    if (label === 'Penta') {
        return 'border-teal-700/40 bg-teal-900/20 text-teal-200'
    }

    if (label === 'Quadra') {
        return 'border-emerald-700/40 bg-emerald-900/20 text-emerald-200'
    }

    return 'border-zinc-800 bg-zinc-950/90 text-transparent'
}

function renderMetricValueGroup(primaryValue, secondaryValue = null, secondaryClassName = 'text-zinc-300') {
    return (
        <div className='ml-auto flex min-w-0 items-center justify-end gap-1 text-zinc-100'>
            <span className='truncate leading-none tabular-nums'>{primaryValue}</span>
            {secondaryValue ? (
                <>
                    <span className='hidden shrink-0 text-zinc-600 md:inline'>&bull;</span>
                    <span className={`hidden shrink-0 text-[9px] leading-none tabular-nums md:inline ${secondaryClassName}`}>{secondaryValue}</span>
                </>
            ) : null}
        </div>
    )
}

function MobileTeamObjectiveChip({ alt, iconPath, isActive = false, onToggle, value }) {
    return (
        <div className="relative flex h-5 items-center">
            <button
                aria-pressed={isActive}
                data-mobile-objective-chip="true"
                className={`inline-flex h-5 shrink-0 items-center gap-1 rounded-full border px-1.5 text-[9px] font-semibold leading-none transition-colors ${isActive ? 'border-zinc-700 bg-zinc-900 text-zinc-100' : 'border-zinc-800 bg-zinc-950/90 text-zinc-100'}`}
                onClick={onToggle}
                type="button"
            >
                <span className="tabular-nums">{value}</span>
                <img alt={alt} className="h-3 w-3 shrink-0 brightness-0 invert" src={`${process.env.PUBLIC_URL}${iconPath}`} />
            </button>
        </div>
    )
}

function MobileTeamAverageRank({ averageElo }) {
    if (!averageElo?.badge) {
        return null
    }

    return (
        <CompactRankLabel badge={averageElo.badge} leaguePoints={averageElo.leaguePoints} />
    )
}

function MobileTeamHeader({ orderedChampions, team }) {
    const [activeObjective, setActiveObjective] = useState(null)

    useEffect(() => {
        if (!activeObjective) {
            return undefined
        }

        function dismissObjectivePopover(event) {
            const target = event.target

            if (target?.closest?.('[data-mobile-objective-chip="true"]')) {
                return
            }

            setActiveObjective(null)
        }

        function dismissForViewportChange() {
            setActiveObjective(null)
        }

        document.addEventListener('pointerdown', dismissObjectivePopover, true)
        window.addEventListener('scroll', dismissForViewportChange, true)
        window.addEventListener('resize', dismissForViewportChange)

        return () => {
            document.removeEventListener('pointerdown', dismissObjectivePopover, true)
            window.removeEventListener('scroll', dismissForViewportChange, true)
            window.removeEventListener('resize', dismissForViewportChange)
        }
    }, [activeObjective])

    function toggleObjectiveLabel(label, event) {
        const buttonRect = event.currentTarget.getBoundingClientRect()
        const nextPopover = {
            label,
            left: Math.min(Math.max(buttonRect.left + (buttonRect.width / 2), 52), window.innerWidth - 52),
            top: Math.max(buttonRect.top - 10, 20),
        }

        setActiveObjective((currentValue) => (
            currentValue?.label === label ? null : nextPopover
        ))
    }

    return (
        <div className="rounded-t-lg border border-zinc-900 bg-black/90 px-2 py-1.5">
            <div className="grid min-h-[20px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2">
                <div className="flex h-5 items-center">
                    <ToneBadge compact label={team.outcomeLabel} tone={team.tone} />
                </div>
                <div className="flex min-w-0 items-center justify-center">
                    <MobileTeamAverageRank averageElo={team.averageElo} />
                </div>
                <div className="flex min-w-0 items-center justify-end">
                    <MobileTeamBanStrip bans={team.bans} orderedChampions={orderedChampions} />
                </div>
            </div>
            <div className="mt-1 flex min-h-[20px] items-center justify-between gap-2">
                <div className="flex min-w-0 items-center">
                    <div className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-zinc-800 bg-zinc-950/90 px-1.5 text-[9px] font-semibold text-zinc-100">
                        <img alt="kda icon" className="h-3 w-3 shrink-0 brightness-0 invert" src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`} />
                        <span className="tabular-nums">{team.kills} / {team.deaths} / {team.assists}</span>
                    </div>
                </div>
                <div className="flex min-w-0 items-center justify-end gap-1 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <MobileTeamObjectiveChip alt="tower icon" iconPath="/assets/minimap_asset_icons/icon_ui_tower_minimap.png" isActive={activeObjective?.label === 'Turrets'} onToggle={(event) => toggleObjectiveLabel('Turrets', event)} value={team.towers} />
                    <MobileTeamObjectiveChip alt="dragon icon" iconPath="/assets/minimap_asset_icons/dragon_minimap_icon.png" isActive={activeObjective?.label === 'Dragons'} onToggle={(event) => toggleObjectiveLabel('Dragons', event)} value={team.dragons} />
                    <MobileTeamObjectiveChip alt="void grub icon" iconPath="/assets/minimap_asset_icons/horde.png" isActive={activeObjective?.label === 'Void Grubs'} onToggle={(event) => toggleObjectiveLabel('Void Grubs', event)} value={team.voidGrubs} />
                    <MobileTeamObjectiveChip alt="rift herald icon" iconPath="/assets/minimap_asset_icons/sru_riftherald_minimap_icon.png" isActive={activeObjective?.label === 'Rift Heralds'} onToggle={(event) => toggleObjectiveLabel('Rift Heralds', event)} value={team.riftHeralds} />
                    <MobileTeamObjectiveChip alt="baron icon" iconPath="/assets/minimap_asset_icons/baron_minimap_icon.png" isActive={activeObjective?.label === 'Barons'} onToggle={(event) => toggleObjectiveLabel('Barons', event)} value={team.barons} />
                </div>
            </div>
            {activeObjective ? (
                createPortal(
                    <div
                        className="pointer-events-none fixed z-[9999]"
                        style={{
                            left: `${activeObjective.left}px`,
                            top: `${activeObjective.top}px`,
                            transform: 'translate(-50%, -100%)',
                        }}
                    >
                        <div className="rounded-md border border-zinc-700 bg-zinc-950/95 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-zinc-200 shadow-[0_10px_24px_-16px_rgba(0,0,0,0.95)]">
                            {activeObjective.label}
                        </div>
                    </div>,
                    document.body,
                )
            ) : null}
        </div>
    )
}

function MobileTeamBanStrip({ bans = [], orderedChampions = [] }) {
    const normalizedBans = [...(bans || [])]
        .sort((left, right) => (left?.pickTurn || 0) - (right?.pickTurn || 0))
        .slice(0, 5)

    while (normalizedBans.length < 5) {
        normalizedBans.push({
            championId: 0,
            pickTurn: normalizedBans.length + 1,
        })
    }

    return (
        <div className="flex items-center gap-1">
            {normalizedBans.map((bannedChampion, index) => {
                    const championNameId = orderedChampions.find((champion) => champion.champion_id === bannedChampion?.championId)?.name_id
                    const hasChampionBan = Number(bannedChampion?.championId) > 0
                    const championIconSource = hasChampionBan && championNameId
                        ? assetUrl(`/champion_icons/${championNameId}.png`)
                        : `${process.env.PUBLIC_URL}/assets/missing_asset/missing_icon.png`

                    return (
                        <div className="h-5 w-5 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900" key={`${bannedChampion?.pickTurn || index}-${bannedChampion?.championId || 0}-${index}`}>
                            <img
                                alt={hasChampionBan ? 'banned champion icon' : 'empty ban slot'}
                                className={`h-full w-full object-cover ${hasChampionBan ? 'scale-[1.22] grayscale' : 'scale-[0.92] opacity-55'}`}
                                onError={(event) => {
                                    event.currentTarget.src = `${process.env.PUBLIC_URL}/assets/missing_asset/missing_icon.png`
                                }}
                                src={championIconSource}
                                style={{ clipPath: 'inset(7%)' }}
                            />
                        </div>
                    )
                })}
        </div>
    )
}

const MatchDetails = ({ orderedChampions, individualStats, matchRecord, displayRegion, navAndSearchParticipant, summonerData, calculateTimeAgo, formatArenaPlacement }) => {
    // # rename to match record
    const [showDetails, setShowDetails] = useState(false)
    const [detailType, setDetailType] = useState("default")
    const match = matchRecord?.archived_match_json
    const isCompleted = matchRecord?.completion_status === 'completed'

    const highlightedPlayer = summonerData?.puuid
    const matchTone = getMatchTone(match, individualStats)
    const matchToneStyles = getToneStyles(matchTone)
    const mobileDetailToggleButtonInactiveClassName = `inline-flex h-7 items-center justify-center rounded-md border px-3 text-[10px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors ${buttonFocusRingClass} border-zinc-700 bg-zinc-950 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-900 hover:text-zinc-100`
    const mobileDetailToggleButtonActiveClassName = `inline-flex h-7 items-center justify-center rounded-md border px-3 text-[10px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors ${buttonFocusRingClass} ${matchTone === 'defeat' ? '!border-rose-700/60 !bg-rose-900/35 !text-rose-200' : matchTone === 'victory' ? '!border-cyan-700/60 !bg-cyan-900/45 !text-cyan-200' : '!border-zinc-700 !bg-zinc-800/90 !text-zinc-200'}`
    const getMobileDetailToggleButtonClassName = (isActive) => (isActive ? mobileDetailToggleButtonActiveClassName : mobileDetailToggleButtonInactiveClassName)
    const mobileExpandButtonClassName = `inline-flex h-5 w-5 items-center justify-center transition-colors hover:text-zinc-100 ${matchToneStyles.badgeText}`


    function displayMatchDetails() {
        setShowDetails((previousState) => !previousState)
    }

    function renderSummonerSpellsMobile(individualStats) {
        return (
            <div className='flex gap-0.5 mb-0.5'>
                <SummonerSpellIconWithFallback width="16" height="16" alt="summoner spell icon" src={assetUrl(`/summoner_spell_icons/${individualStats?.summoner1Id}.png`)} />
                <SummonerSpellIconWithFallback width="16" height="16" alt="summoner spell icon" src={assetUrl(`/summoner_spell_icons/${individualStats?.summoner2Id}.png`)} />
            </div>
        )
    }

    function renderAugmentIconsMobile(individualStats) {

        return (
            <div className='flex'>
                <div className='mr-0.5 mb-0.5'>
                    <AugmentIconWithFallback individualStats={individualStats} matchRecord={matchRecord} format='mb-0.5' width="16" height="16" alt="augment icon" src={assetUrl(`/augment_icons/${individualStats?.playerAugment1}.png`)} />
                    <AugmentIconWithFallback individualStats={individualStats} matchRecord={matchRecord} width="16" height="16" alt="augment icon" src={assetUrl(`/augment_icons/${individualStats?.playerAugment2}.png`)} />
                </div>
                <div className='mr-0.5 mb-0.5'>
                    <AugmentIconWithFallback individualStats={individualStats} matchRecord={matchRecord} format='mb-0.5' width="16" height="16" alt="augment icon" src={assetUrl(`/augment_icons/${individualStats?.playerAugment3}.png`)} />
                    <AugmentIconWithFallback individualStats={individualStats} matchRecord={matchRecord} width="16" height="16" alt="augment icon" src={assetUrl(`/augment_icons/${individualStats?.playerAugment4}.png`)} />
                </div>
                <div className='mr-0.5 mb-0.5'>
                    <AugmentIconWithFallback individualStats={individualStats} matchRecord={matchRecord} format='mb-0.5' width="16" height="16" alt="augment icon" src={assetUrl(`/augment_icons/${individualStats?.playerAugment5}.png`)} />
                    <AugmentIconWithFallback individualStats={individualStats} matchRecord={matchRecord} width="16" height="16" alt="augment icon" src={assetUrl(`/augment_icons/${individualStats?.playerAugment6}.png`)} />
                </div>
            </div>
        )
    }

    function calculateParticipantKp(individualStats, matchRecord) {
        let kp = String(individualStats?.challenges?.perfectGame) === "1" ? "100" : (individualStats?.challenges?.killParticipation)?.toFixed(2).substring(2)
        if (individualStats?.challenges?.kda === 0){
            kp = "0"
        }

        return `${individualStats?.kills}/${individualStats?.deaths}/${individualStats?.assists}${!isArenaQueue(matchRecord?.queueId) ? ` (${kp}%)` : ''}`
    }

    function calculateParticipantScoreline(individualStats) {
        return `${individualStats?.kills}/${individualStats?.deaths}/${individualStats?.assists}`
    }

    function calculateParticipantKpPercent(individualStats, matchRecord) {
        if (isArenaQueue(matchRecord?.queueId)) {
            return ''
        }

        let kp = String(individualStats?.challenges?.perfectGame) === "1" ? "100" : (individualStats?.challenges?.killParticipation)?.toFixed(2).substring(2)
        if (individualStats?.challenges?.kda === 0) {
            kp = "0"
        }

        return `${kp}%`
    }

    function getGameDurationMinutes(matchRecord) {
        const durationSeconds = Number(matchRecord?.archived_match_json?.info?.gameDuration || matchRecord?.game_duration || 0)
        return durationSeconds > 0 ? durationSeconds / 60 : 0
    }

    function calculateParticipantCsPerMinute(participant, matchRecord) {
        const minutes = getGameDurationMinutes(matchRecord)
        const totalCs = Number(participant?.neutralMinionsKilled || 0) + Number(participant?.totalMinionsKilled || 0)

        return minutes > 0 ? formatCompactRateValue(totalCs / minutes) : '0'
    }

    function calculateParticipantGoldPerMinute(participant) {
        return String(Math.round(Number(participant?.challenges?.goldPerMinute || 0)))
    }

    function renderParticipantItemIcons(individualStats, matchRecord, options = {}) {
        const {
            includeRoleBoundItem = true,
        } = options
        const items = [0, 0, 0, 0, 0, 0, 0]; // There are always items 0-5 + 6 for ward slot
        let nonZeroCount = 0;
        for (let i = 0; i <= 6; i++) {
            const key = 'item' + i;
            const value = individualStats[key];
            if (value !== undefined && value !== 0) {
                if (i === 6) {
                    items[6] = value;
                } else {
                    items[nonZeroCount++] = value;
                }
            }
        }
        return (
            <div className='flex gap-0.5'>
                {items.map((item, index) => (
                    <ParticipantItemIconsWithFallBack
                        key={index}
                        individualStats={individualStats}
                        match={matchRecord}
                        className="h-[18px] w-[18px] rounded-md"
                        // width={width}
                        // height={height}
                        // src={process.env.PUBLIC_URL + `/assets/item_icons/${item}.png`}
                        src={assetUrl(`/item_icons/${item}.png`)}
                    />
                ))}
                {includeRoleBoundItem ? (
                    <ParticipantItemIconsWithFallBack
                        individualStats={individualStats}
                        match={matchRecord}
                        className="h-[18px] w-[18px] rounded-md"
                        src={assetUrl(`/item_icons/${individualStats?.roleBoundItem}.png`)}
                    />
                ) : null}
            </div>
        )
    }

    function getParticipantPerformanceBadgeLabel(participant) {
        if ((participant?.pentaKills || 0) > 0) {
            return 'Penta'
        }

        if ((participant?.quadraKills || 0) > 0) {
            return 'Quadra'
        }

        return ''
    }

    function renderParticipantStatMatrix(participant, matchRecord) {
        const isArena = isArenaQueue(matchRecord?.queueId)
        const killParticipationText = calculateParticipantKpPercent(participant, matchRecord)
        const totalCs = participant?.neutralMinionsKilled + participant?.totalMinionsKilled
        const csPerMinute = calculateParticipantCsPerMinute(participant, matchRecord)
        const formattedDamageDealt = `${(Number(participant?.totalDamageDealtToChampions || 0) / 1000).toFixed(1)}k`
        const formattedDamageTaken = `${(Number(participant?.totalDamageTaken || 0) / 1000).toFixed(1)}k`
        const performanceBadgeLabel = getParticipantPerformanceBadgeLabel(participant)
        const goldPerMinute = calculateParticipantGoldPerMinute(participant)

        return (
            <div className={`${MOBILE_DETAIL_STAT_MATRIX_CLASS_NAME} ml-auto`}>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    <img alt="kda icon" className='h-3 w-3 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-kda-icon.svg`} />
                    {renderMetricValueGroup(calculateParticipantScoreline(participant))}
                </div>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    <img alt="kda value icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-sword-icon.svg`} />
                    <div className='ml-auto flex min-w-0 items-center justify-end gap-1 text-zinc-100'>
                        <KdaValue className="shrink-0 leading-none tabular-nums text-zinc-100" iconSize={13} isPerfectKda={Boolean(participant?.challenges?.perfectGame) && Number(participant?.challenges?.kda || 0) !== 0} value={formatKdaValue(participant)} />
                        {!isArena ? (
                            <>
                                <span className='hidden shrink-0 text-zinc-600 md:inline'>&bull;</span>
                                <span className='hidden shrink-0 text-[9px] leading-none tabular-nums text-zinc-300 md:inline'>{killParticipationText}</span>
                            </>
                        ) : null}
                    </div>
                </div>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    <img alt="minions slain icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/minions-slain.svg`} />
                    {renderMetricValueGroup(totalCs, csPerMinute)}
                </div>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    <img alt="damage dealt icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-sword-icon.svg`} />
                    {renderMetricValueGroup(formattedDamageDealt)}
                </div>
                <div className='flex h-[22px] w-full items-center'>
                    {performanceBadgeLabel ? (
                        <div className={`inline-flex h-[22px] w-full items-center justify-center rounded-full border px-2 text-center text-[8.5px] font-semibold uppercase tracking-[0.12em] leading-none whitespace-nowrap ${getPerformanceBadgeStyles(performanceBadgeLabel)}`}>
                            {performanceBadgeLabel}
                        </div>
                    ) : <div aria-hidden="true" className='h-[22px] w-full' />}
                </div>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    {!isArena ? (
                        <div className='flex min-w-0 items-center gap-1'>
                            <IoEye className='h-3 w-3 shrink-0 text-zinc-100' />
                            <span className='hidden shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em] text-zinc-300 md:inline'>VIS</span>
                        </div>
                    ) : <img alt="coins icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-coins-icon.svg`} />}
                    {renderMetricValueGroup(!isArena ? participant?.visionScore : formatGoldValue(participant))}
                </div>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    <img alt="coins icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-coins-icon.svg`} />
                    {renderMetricValueGroup(formatGoldValue(participant), goldPerMinute)}
                </div>
                <div className={MOBILE_DETAIL_SMALL_PILL_CLASS_NAME}>
                    <img alt="damage taken icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-stat-switcher-shield.svg`} />
                    {renderMetricValueGroup(formattedDamageTaken)}
                </div>
            </div>
        )
    }

    // function renderChampionParticipantIcon(individualStats, match) {
    //     const championName = checkIfChampionIsKaynTransformation(individualStats?.championName, individualStats?.championTransform)
    //     return (
    //         <div className="h-8 w-8" style={{ position: 'relative', display: 'inline-block' }}>
    //             {/* <img width={width} height={height} alt="champion icon" src={process.env.PUBLIC_URL + `/assets/champion/${individualStats?.championName}.png`} /> */}
    //             <div className={`rounded-full ${(match.info?.endOfGameResult === "Abort_Unexpected" || individualStats?.gameEndedInEarlySurrender) ? "bg-gray-700" : individualStats?.win ? " bg-cyan-900" : "bg-rose-950"} h-4 w-4 flex justify-center items-center font-bold`} style={{ position: 'absolute', bottom: '0', right: '0', fontSize: '10px' }}>
    //                 <div>{individualStats?.champLevel}</div>
    //             </div>
    //         </div>
    //     )
    // }

    function renderChampionParticipantIcon(individualStats, matchRecord) {
        const championName = checkIfChampionIsKaynTransformation(individualStats?.championName, individualStats?.championTransform)
        const toneStyles = getToneStyles(getMatchTone(matchRecord?.archived_match_json, individualStats))

        return (
            <div className="h-10 w-10" style={{ position: 'relative', display: 'inline-block' }}>
                <div className={`relative rounded-full overflow-hidden border ${toneStyles.mutedBorder} bg-zinc-950`}>
                    <img
                        className="object-cover w-full h-full scale-[1.3]"
                        alt="champion icon"
                        onError={(event) => {
                            event.currentTarget.src = `${process.env.PUBLIC_URL}/assets/missing_asset/missing_icon.png`
                        }}
                        src={assetUrl(`/champion_icons/${championName}.png`)}
                    />
                </div>
                <div className="absolute -bottom-1 right-0 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-zinc-900/95 text-zinc-200" style={{ fontSize: '10px', fontWeight: 700 }}>
                    <div>{individualStats?.champLevel}</div>
                </div>
            </div>
        )
    }

    function renderParticipantEloBadge(badge) {
        return <CompactRankLabel badge={badge} className="text-[10px]" showLeaguePoints={false} />
    }

    function renderParticipantSummonerSpells(participant, matchRecord) {
        const tone = getMatchTone(matchRecord?.archived_match_json, participant)

        return (
            <div className='flex h-[22px] items-center justify-center'>
                <SpellIcons
                    className={MOBILE_DETAIL_ICON_ROW_CLASS_NAME}
                    size={20}
                    sources={[
                        assetUrl(`/summoner_spell_icons/${participant?.summoner1Id}.png`),
                        assetUrl(`/summoner_spell_icons/${participant?.summoner2Id}.png`),
                    ]}
                    tone={tone}
                />
            </div>
        )
    }


    
    function renderParticipantRuneIcons(participant, matchRecord) {
        const perks = participant?.perks?.styles
        const tone = getMatchTone(matchRecord?.archived_match_json, participant)

        return (
            <div className='flex h-[22px] items-center justify-center'>
                <RuneIcons
                    className={MOBILE_DETAIL_ICON_ROW_CLASS_NAME}
                    primaryClassName="scale-[1.04] object-cover"
                    primarySize={20}
                    secondaryClassName="scale-[0.88] object-contain"
                    secondaryInsetClassName=""
                    secondarySize={20}
                    sources={{
                        primary: assetUrl(`/major_and_minor_rune_icons/trees/${perks?.[0]?.style}/slot_1/${perks?.[0]?.selections?.[0]?.perk}.png`),
                        secondary: assetUrl(`/major_and_minor_rune_icons/trees/${perks?.[1]?.style}.png`),
                    }}
                    tone={tone}
                />
            </div>
        )
    }



    function renderParticipantDamage(individualStats) {
        return (
            <div className={MOBILE_DETAIL_STAT_CARD_CLASS_NAME}>
                <div className={MOBILE_DETAIL_METRIC_LINE_CLASS_NAME}>
                    <img alt="damage dealt icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-sword-icon.svg`} />
                    <span className='truncate text-zinc-100'>{(Number(individualStats?.totalDamageDealtToChampions || 0) / 1000).toFixed(1)}k</span>
                </div>
                <div className={`${MOBILE_DETAIL_METRIC_LINE_CLASS_NAME} mt-1`}>
                    <img alt="damage taken icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-stat-switcher-shield.svg`} />
                    <span className='truncate text-zinc-100'>{(Number(individualStats?.totalDamageTaken || 0) / 1000).toFixed(1)}k</span>
                </div>
            </div>
        )
    }

    function renderArenaTeamNames(subTeamId) {
        const teamMap = {
            1: "Poro",
            2: "Minion",
            3: "Scuttle",
            4: "Krug",
            5: "Raptor",
            6: "Sentinel",
            7: "Wolf",
            8: "Gromp"
        }

        const teamName = teamMap[subTeamId]

        return teamName
    }

    function buildArenaTeamGroups(participants = []) {
        const groupedTeams = new Map()

        participants.forEach((participant) => {
            const subteamId = Number(participant?.playerSubteamId || 0)
            const existingTeam = groupedTeams.get(subteamId)

            if (existingTeam) {
                existingTeam.participants.push(participant)
                existingTeam.placement = Math.min(existingTeam.placement, Number(participant?.placement || 99))
                return
            }

            groupedTeams.set(subteamId, {
                subteamId,
                teamName: renderArenaTeamNames(subteamId),
                placement: Number(participant?.placement || 99),
                participants: [participant],
            })
        })

        return [...groupedTeams.values()]
            .map((team) => ({
                ...team,
                participants: [...team.participants].sort((left, right) => Number(left?.placement || 99) - Number(right?.placement || 99)),
            }))
            .sort((left, right) => left.placement - right.placement)
    }

    function renderArenaTriosParticipantRow(participant, match) {
        const participantTone = getToneStyles(getMatchTone(match, participant))
        const isFocus = participant?.puuid === highlightedPlayer

        return (
            <div className={`rounded-md border px-2 py-1.5 ${isFocus ? participantTone.rowHighlightSurface : participantTone.rowSurface}`}>
                <div className='grid w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-1'>
                    <div className='flex h-[18px] min-w-0 flex-1 items-center gap-1'>
                        <div className='flex h-[18px] shrink-0 items-center'>{renderParticipantEloBadge(participant?.wrgg?.elo?.badge)}</div>
                        <Link
                            className='min-w-0 truncate text-[12.5px] font-medium leading-none text-zinc-200'
                            to={`/summoners/${displayRegion}/${participant.riotIdGameName}-${participant.riotIdTagline}`}
                            onClick={navAndSearchParticipant}
                        >
                            {participant.riotIdGameName + " #" + participant.riotIdTagline}
                        </Link>
                    </div>
                    <div className='flex shrink-0 items-center justify-self-end'>
                        {renderParticipantItemIcons(participant, matchRecord, { includeRoleBoundItem: false })}
                    </div>
                </div>
                <div className='mt-1 grid w-full grid-cols-[42px_minmax(0,1fr)_max-content] items-center gap-x-1 gap-y-0.5'>
                    <div className='flex h-full items-center justify-center'>
                        {renderChampionParticipantIcon(participant, match)}
                    </div>
                    <div className='flex min-w-0 items-center overflow-visible'>
                        <AugmentGrid className="ml-1" columns={3} size={16} sources={[
                            assetUrl(`/augment_icons/${participant?.playerAugment1}.png`),
                            assetUrl(`/augment_icons/${participant?.playerAugment2}.png`),
                            assetUrl(`/augment_icons/${participant?.playerAugment3}.png`),
                            assetUrl(`/augment_icons/${participant?.playerAugment4}.png`),
                            assetUrl(`/augment_icons/${participant?.playerAugment5}.png`),
                            assetUrl(`/augment_icons/${participant?.playerAugment6}.png`),
                        ]} tone={getMatchTone(match, participant)} />
                    </div>
                    <div>
                        {renderParticipantDamage(participant)}
                    </div>
                </div>
            </div>
        )
    }

    // assumed 5v5 Game Mode(DESKTOP / ORIGINAL)
    // function renderParticipantsDetail(match) {
    //     let blueSide = []
    //     let redSide = []
    //     let arenaTeams = []

    //     if (match?.info?.queueId !== 1700) {
    //         blueSide = match?.info?.participants?.filter((participant) => {
    //             return parseInt(participant.teamId) === 100
    //         })
    //         redSide = match?.info?.participants?.filter((participant) => {
    //             return parseInt(participant.teamId) === 200
    //         })
    //     } else if (match?.info?.queueId === 1700) {
    //         arenaTeams = match?.info?.participants
    //         arenaTeams.sort((a, b) => a.placement - b.placement);
    //     }

    //     // Non-Arena Format
    //     if (match?.info?.queueId !== 1700) {
    //         return (
    //             <div className='mx-2 border border-pink-400'>
    //                 <div className='flex gap-2'>
    //                     <button className={`px-2 py-1 ${showBuildPath === false ? "bg-cyan-900" : "bg-zinc-600"} whitespace-nowrap font-semibold text-xs rounded hover:bg-cyan-900`} onClick={() => setShowBuildPath(false)}>DETAILS</button>
    //                     <button className={`px-2 py-1 ${showBuildPath === true ? "bg-cyan-900" : "bg-zinc-600"} whitespace-nowrap font-semibold text-xs rounded hover:bg-cyan-900`} onClick={() => setShowBuildPath(true)}>BUILD</button>
    //                 </div>
    //                 {showBuildPath === false ?
    //                     <div>
    //                         <h3>Blue Team:</h3>
    //                         {blueSide?.map((participant, index) => {
    //                             return (
    //                                 <div key={index}>
    //                                     {renderChampionIcon(participant, "25", "25", true)}
    //                                     <Link className='linkStyle' to={`/summoners/${params.region}/${params.platform}/${participant.riotIdGameName}/${participant.riotIdTagline}`} onClick={navAndSearchParticipant} >{participant.riotIdGameName + " #" + participant.riotIdTagline}<br></br></Link>
    //                                     <>{renderParticipantItemIcons(participant)}</>
    //                                     {calculateKda(participant)}
    //                                     {calculateParticipantCsAndGold(participant, match)}
    //                                     <plaintext>Damage: {participant?.totalDamageDealtToChampions}</plaintext>
    //                                     <plaintext>Damage Taken: {participant?.totalDamageTaken}</plaintext>
    //                                     <plaintext>Control Wards Placed: {participant?.challenges?.controlWardsPlaced}</plaintext>
    //                                     <plaintext>{participant?.wardsPlaced} / {participant?.wardsKilled}</plaintext>
    //                                 </div>
    //                             )
    //                         })}
    //                         <h3>Red Team:</h3>
    //                         {redSide?.map((participant, index) => {
    //                             return (
    //                                 <div key={index}>
    //                                     {renderChampionIcon(participant, "25", "25")}
    //                                     <Link to={`/summoners/${params.region}/${params.platform}/${participant.riotIdGameName}/${participant.riotIdTagline}`} onClick={navAndSearchParticipant}>{participant.riotIdGameName + " #" + participant.riotIdTagline}<br></br></Link>
    //                                     <>{renderParticipantItemIcons(participant)}</>
    //                                     {calculateKda(participant)}
    //                                     {calculateParticipantCsAndGold(participant, match)}
    //                                     <plaintext>Damage: {participant?.totalDamageDealtToChampions}</plaintext>
    //                                     <plaintext>Damage Taken: {participant?.totalDamageTaken}</plaintext>
    //                                     <plaintext>Control Wards Placed: {participant?.challenges?.controlWardsPlaced}</plaintext>
    //                                     <plaintext>{participant?.wardsPlaced} / {participant?.wardsKilled}</plaintext>
    //                                 </div>
    //                             )
    //                         })}
    //                     </div>
    //                     :
    //                     <div>
    //                         {console.log(match?.info?.participants?.filter((player) => player?.puuid === highlightedPlayer && player?.puuid)?.[0]?.buildPath, "check this")}
    //                         {match?.info?.participants?.filter((player) => {
    //                             return (player?.puuid === highlightedPlayer && player?.puuid)
    //                         })?.[0]?.buildPath?.map((item, index) => {
    //                             return (
    //                                 <div key={index}>
    //                                     <div>{item?.itemId}</div>
    //                                     <div>meta</div>
    //                                 </div>
    //                             )
    //                         })}
    //                     </div>
    //                 }
    //             </div>
    //         )
    //         /// If Game Mode is Arena return different JSX
    //     } else if (match?.info?.queueId === 1700) {
    //         return (
    //             <div>
    //                 <h3>Arena Teams:</h3>
    //                 {arenaTeams.map((participant, index) => {
    //                     return (
    //                         <div key={index}>
    //                             <plaintext>{participant?.placement}</plaintext>
    //                             {renderChampionIcon(participant, "25", "25")}
    //                             <Link to={`/summoners/${params.region}/${params.platform}/${participant.riotIdGameName}/${participant.riotIdTagline}`} onClick={navAndSearchParticipant}>{participant.riotIdGameName + " #" + participant.riotIdTagline}<br></br></Link>
    //                             <>{renderParticipantItemIcons(participant)}</>
    //                             <>A1{participant?.playerAugment1}</>
    //                             <>A2{participant?.playerAugment1}</>
    //                             <>A3{participant?.playerAugment1}</>
    //                             <>A4{participant?.playerAugment1}</>
    //                             {calculateKda(participant)}
    //                             {calculateParticipantCsAndGold(participant, match)}
    //                             <plaintext>Damage: {participant?.totalDamageDealtToChampions}</plaintext>
    //                             <plaintext>Damage Taken: {participant?.totalDamageTaken}</plaintext>
    //                             <plaintext>Control Wards Placed: {participant?.challenges?.controlWardsPlaced}</plaintext>
    //                             <plaintext>{participant?.wardsPlaced} / {participant?.wardsKilled}</plaintext>
    //                         </div>
    //                     )
    //                 })}
    //             </div>
    //         )
    //     } else {
    //         return null
    //     }
    // }



    function renderParticipantsDetailMobile(match) {
        let blueSide = []
        let redSide = []
        let arenaTeams = []
        const queueId = Number(match?.info?.queueId || 0)
        const isTriosArena = isTriosArenaQueue(queueId)
        const isArena = isArenaQueue(queueId)
        const arenaVictoryPlacement = getArenaVictoryPlacement(queueId)

        if (!isArena) {
            blueSide = match?.info?.participants?.filter((participant) => {
                return parseInt(participant.teamId) === 100
            })
            redSide = match?.info?.participants?.filter((participant) => {
                return parseInt(participant.teamId) === 200
            })
        } else {
            arenaTeams = match?.info?.participants
            arenaTeams.sort((a, b) => a.placement - b.placement);
        }

        let blueSideKills = match?.info?.teams?.[0]?.objectives?.champion?.kills
        let blueSideDeaths = 0
        let blueSideAssists = 0
        let blueSideTurretKills = match?.info?.teams?.[0]?.objectives?.tower?.kills
        let blueSideBaronKills = match?.info?.teams?.[0]?.objectives?.baron?.kills
        let blueSideDragonKills = match?.info?.teams?.[0]?.objectives?.dragon?.kills
        let blueSideVoidGrubKills = match?.info?.teams?.[0]?.objectives?.horde?.kills
        let blueSideRiftHeraldKills = match?.info?.teams?.[0]?.objectives?.riftHerald?.kills
        blueSide?.forEach((player) => {
            blueSideDeaths += player?.deaths
            blueSideAssists += player?.assists
        })

        let redSideKills = match?.info?.teams?.[1]?.objectives?.champion?.kills
        let redSideDeaths = 0
        let redSideAssists = 0
        let redSideTurretKills = match?.info?.teams?.[1]?.objectives?.tower?.kills
        let redSideBaronKills = match?.info?.teams?.[1]?.objectives?.baron?.kills
        let redSideDragonKills = match?.info?.teams?.[1]?.objectives?.dragon?.kills
        let redSideVoidGrubKills = match?.info?.teams?.[1]?.objectives?.horde?.kills
        let redSideRiftHeraldKills = match?.info?.teams?.[1]?.objectives?.riftHerald?.kills
        redSide?.forEach((player) => {
            redSideDeaths += player?.deaths
            redSideAssists += player?.assists
        })


        // Non-Arena Format
        if (!isArena) {
            return (
                <div className='mx-2'>
                    <div className='flex gap-1.5 border-b border-zinc-800/80 pb-2'>
                        <button
                            className={getMobileDetailToggleButtonClassName(detailType === "default")}
                            onClick={() => setDetailType("default")}
                            type="button"
                        >
                            DETAILS
                        </button>
                        <button
                            className={getMobileDetailToggleButtonClassName(detailType === "build")}
                            onClick={() => setDetailType("build")}
                            type="button"
                        >
                            BUILD
                        </button>
                        {match?.wrgg?.teamState?.checkpoints?.length ? (
                            <button
                                className={getMobileDetailToggleButtonClassName(detailType === "teamState")}
                                onClick={() => setDetailType("teamState")}
                                type="button"
                            >
                                TEAM
                            </button>
                        ) : null}
                    </div>
                    {detailType === "default" && (
                        <div className='mt-2 space-y-2'>
                            {[
                                {
                                    averageElo: match?.info?.teams?.find((team) => team?.teamId === 100)?.averageElo || null,
                                    assists: blueSideAssists,
                                    barons: blueSideBaronKills,
                                    deaths: blueSideDeaths,
                                    dragons: blueSideDragonKills,
                                    kills: blueSideKills,
                                    outcomeLabel: (match?.info?.endOfGameResult === "Abort_Unexpected" || individualStats?.gameEndedInEarlySurrender) ? "REMAKE" : blueSide?.[0]?.win ? "VICTORY" : "DEFEAT",
                                    participants: blueSide,
                                    bans: match?.info?.teams?.find((team) => team?.teamId === 100)?.bans || [],
                                    riftHeralds: blueSideRiftHeraldKills,
                                    tone: getMatchTone(match, blueSide?.[0]),
                                    towers: blueSideTurretKills,
                                    voidGrubs: blueSideVoidGrubKills,
                                },
                                {
                                    averageElo: match?.info?.teams?.find((team) => team?.teamId === 200)?.averageElo || null,
                                    assists: redSideAssists,
                                    barons: redSideBaronKills,
                                    deaths: redSideDeaths,
                                    dragons: redSideDragonKills,
                                    kills: redSideKills,
                                    outcomeLabel: (match?.info?.endOfGameResult === "Abort_Unexpected" || individualStats?.gameEndedInEarlySurrender) ? "REMAKE" : redSide?.[0]?.win ? "VICTORY" : "DEFEAT",
                                    participants: redSide,
                                    bans: match?.info?.teams?.find((team) => team?.teamId === 200)?.bans || [],
                                    riftHeralds: redSideRiftHeraldKills,
                                    tone: getMatchTone(match, redSide?.[0]),
                                    towers: redSideTurretKills,
                                    voidGrubs: redSideVoidGrubKills,
                                },
                            ].map((team, teamIndex) => {
                                if (!team.participants?.length) {
                                    return null
                                }

                                return (
                                    <div key={teamIndex}>
                                        <MobileTeamHeader orderedChampions={orderedChampions} team={team} />
                                        <div className='overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
                                            <div className='w-max min-w-full'>
                                                {team.participants?.map((participant, index) => {
                                                    const participantTone = getToneStyles(getMatchTone(match, participant))
                                                    const isFocus = participant?.puuid === highlightedPlayer

                                                    return (
                                                        <div
                                                            key={participant?.puuid || `${teamIndex}-${index}`}
                                                            className={`border-l border-r border-t px-2 py-1.5 ${isFocus ? participantTone.rowHighlightSurface : participantTone.rowSurface} ${index === team.participants.length - 1 ? 'rounded-b-lg border-b' : ''}`}
                                                        >
                                                            <div className='grid w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-1'>
                                                                <div className='flex h-[18px] min-w-0 flex-1 items-center gap-1'>
                                                                    <div className='flex h-[18px] shrink-0 items-center'>{renderParticipantEloBadge(participant?.wrgg?.elo?.badge)}</div>
                                                                    <Link
                                                                        className='min-w-0 truncate text-[12.5px] font-medium leading-none text-zinc-200'
                                                                        to={`/summoners/${displayRegion}/${participant.riotIdGameName}-${participant.riotIdTagline}`}
                                                                        onClick={navAndSearchParticipant}
                                                                    >
                                                                        {participant.riotIdGameName + " #" + participant.riotIdTagline}
                                                                    </Link>
                                                                </div>
                                                                <div className='flex shrink-0 items-center gap-1 justify-self-end'>
                                                                    {isCompleted ? <PerformanceRankBadge compact rank={participant?.wrgg?.performance?.rank} /> : null}
                                                                    {renderParticipantItemIcons(participant, matchRecord)}
                                                                </div>
                                                            </div>
                                                            <div className='mt-1 grid w-full min-w-full grid-cols-[42px_42px_minmax(0,1fr)] grid-rows-2 items-center gap-x-1 gap-y-0.5'>
                                                                <div className='row-span-2 flex h-full items-center justify-center'>
                                                                    {renderChampionParticipantIcon(participant, match)}
                                                                </div>
                                                                <div className='col-start-2 row-start-1 flex h-full items-center justify-center'>
                                                                    {renderParticipantSummonerSpells(participant, matchRecord)}
                                                                </div>
                                                                <div className='col-start-2 row-start-2 flex h-full items-center justify-center'>
                                                                    {renderParticipantRuneIcons(participant, matchRecord)}
                                                                </div>
                                                                <div className='col-start-3 row-span-2 min-w-0 flex h-full items-center'>
                                                                    {renderParticipantStatMatrix(participant, matchRecord)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                    }
                    {detailType === "build" && (
                        <BuildAndTimeline  highlightedPlayer={highlightedPlayer} individualStats={individualStats} match={matchRecord?.archived_match_json} />
                        )
                    }
                    {detailType === "teamState" && (
                        <TeamGoldChart
                            playerTeamId={individualStats?.teamId}
                            teamState={match?.wrgg?.teamState}
                        />
                    )}
                    <div className='h-2'></div>
                </div>
            )
            /// If Game Mode is Arena return different JSX
        } else if (isTriosArena) {
            const groupedArenaTeams = buildArenaTeamGroups(arenaTeams)

            return (
                <>
                    <div className='mx-2 overflow-hidden rounded-xl border border-zinc-800 bg-black'>
                        {groupedArenaTeams.map((team, index) => {
                            const teamTone = team.placement <= arenaVictoryPlacement ? 'victory' : 'defeat'

                            return (
                                <div
                                    key={`${team.subteamId}-${team.placement}`}
                                    className={`${index > 0 ? 'border-t border-zinc-800' : ''} ${teamTone === 'victory' ? 'bg-cyan-950/10' : 'bg-rose-950/10'}`}
                                >
                                    <div className={`flex items-center gap-3 border-b border-zinc-800 px-2 py-1 text-sm font-semibold italic text-zinc-300 ${teamTone === 'victory' ? 'bg-cyan-950/20' : 'bg-rose-950/20'}`}>
                                        <div>{team.placement !== 0 && formatArenaPlacement(team.placement)}</div>
                                        <div className='text-xs'>Team {team.teamName}</div>
                                        <img alt="arena team icon" height="16" width="16" src={process.env.PUBLIC_URL + `/assets/arena_team_icons/Team${team.teamName}.png`} />
                                    </div>
                                    <div className={`space-y-1.5 px-1 py-1.5 ${teamTone === 'victory' ? 'bg-cyan-950/10' : 'bg-rose-950/10'}`}>
                                        {team.participants.map((participant) => renderArenaTriosParticipantRow(participant, match))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <div className='h-2'></div>
                </>
            )
        } else if (match?.info?.queueId === 1700) {
            return (
                <>
                    <div className='mx-2 overflow-hidden rounded-xl border border-zinc-800 bg-black'>
                        {/* <h3>Arena Teams:</h3> Grouped by pairs in order of placemet*/}
                        {arenaTeams.map((participant, index) => {
                            const isFocus = summonerData?.gameName?.toLowerCase() === participant?.riotIdGameName?.toLowerCase()
                            const teamTone = participant?.placement <= 4 ? 'victory' : 'defeat'

                            return (
                                <div
                                    key={index}
                                    className={`${index > 0 ? 'border-t border-zinc-800' : ''} ${isFocus
                                        ? (teamTone === 'victory' ? 'bg-cyan-950/45' : 'bg-rose-950/45')
                                        : 'bg-zinc-900/55'}`}
                                >
                                    {index % 2 === 0 && (
                                        <div className={`flex items-center gap-3 border-b border-zinc-800 px-2 py-1 text-sm font-semibold italic text-zinc-300 ${teamTone === 'victory' ? 'bg-cyan-950/20' : 'bg-rose-950/20'}`}>
                                            <div>{participant?.placement !== 0 && formatArenaPlacement(participant?.placement)}</div>
                                            <div className='text-xs'>Team {renderArenaTeamNames(participant?.playerSubteamId)}</div>
                                            <img alt="arena team icon" height="16" width="16" src={process.env.PUBLIC_URL + `/assets/arena_team_icons/Team${renderArenaTeamNames(participant?.playerSubteamId)}.png`} />
                                        </div>
                                    )}
                                    <div className='mx-1 flex justify-between mb-1 pt-1'>
                                        <div className='flex items-center gap-1 text-sm whitespace-nowrap overflow-hidden'>
                                            <div>{renderParticipantEloBadge(participant?.wrgg?.elo?.badge)}</div>
                                            <Link className='font-bold whitespace-nowrap overflow-hidden text-ellipsis' to={`/summoners/${displayRegion}/${participant.riotIdGameName}-${participant.riotIdTagline}`} onClick={navAndSearchParticipant} >{participant.riotIdGameName + " #" + participant.riotIdTagline}</Link>
                                        </div>
                                        <div className='flex items-center min-w-[145px]'>{renderParticipantItemIcons(participant, matchRecord, { includeRoleBoundItem: false })}</div>
                                    </div>
                                    <div className='mx-1 flex justify-between text-xs pb-1'>
                                        <div className='flex'>
                                            <div className='mr-0.5'>
                                                {renderChampionParticipantIcon(participant, match)}
                                            </div>
                                            <div className='flex min-w-[72px]'>
                                                <div className='mr-0.5'>{renderSummonerSpellsMobile(participant)}</div>
                                                {renderAugmentIconsMobile(participant)}
                                            </div>
                                        </div>
                                        <div className='w-[96px]'>
                                            <div className={MOBILE_DETAIL_STAT_CARD_CLASS_NAME}>
                                                <div className={MOBILE_DETAIL_METRIC_LINE_CLASS_NAME}>
                                                    <img alt="kda icon" className='h-3.5 w-3.5 shrink-0 filter brightness-0 invert' src={process.env.PUBLIC_URL + `/assets/scoreboard_icons/scoreboard-kda-icon.svg`} />
                                                    <KdaValue className="text-zinc-100" iconSize={13} isPerfectKda={Boolean(participant?.challenges?.perfectGame) && Number(participant?.challenges?.kda || 0) !== 0} value={participant?.challenges?.kda || 0} />
                                                </div>
                                                <div className='mt-1 truncate leading-none text-zinc-100'>
                                                    {calculateParticipantKp(participant, matchRecord)}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            {renderParticipantDamage(participant)}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <div className='h-2'></div>
                </>
            )
        } else {
            return null
        }
    }


    return (
        <div>
            <div className={`flex items-center justify-between gap-2 rounded-b-lg border-t border-zinc-800 ${MOBILE_CARD_CHROME_INNER_CLASS_NAME} text-[10px]`}>
                <div className='flex items-center gap-1.5 text-zinc-300'>
                    <div className='leading-none'>
                        {calculateTimeAgo(matchRecord)}
                    </div>
                    <div className='italic leading-none text-zinc-300'>
                        {matchRecord?.match_id}
                    </div>
                </div>
                <div className='flex items-center justify-end'>
                    {showDetails ?
                        <button
                            className={mobileExpandButtonClassName}
                            onClick={displayMatchDetails}
                            type="button"
                        >
                            <IoCaretUpSharp className='h-3.5 w-3.5' />
                        </button>
                        :
                        <button
                            className={mobileExpandButtonClassName}
                            onClick={displayMatchDetails}
                            type="button"
                        >
                            <IoCaretDownSharp className='h-3.5 w-3.5' />
                        </button>
                    }
                </div>
            </div>
            {showDetails ? renderParticipantsDetailMobile(match) : null}
        </div>
    )
}

export default MatchDetails
